import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FilterIcon, LayoutGridIcon, LayoutListIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, UsersIcon, BriefcaseIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import BackButton from '../UI/BackButton';
import { SearchWithAutocomplete } from '../UI/SearchWithAutocomplete';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../UI/FilterSidebar';
import { ProfileCard } from '../Directory/ProfileCard';
import { OpportunityPreviewCard, OpportunityItem } from './OpportunityPreviewCard';
import { connectionProfiles } from '../../pages/connections/data';
import { VendorType } from '../../pages/connections/types';
interface ConnectionPageLayoutProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  vendorType: VendorType;
  filterSections: FilterSection[];
  opportunities: OpportunityItem[];
  opportunitiesLink: string;
  formContent: React.ReactNode;
  formCTALabel: string;
  formCTADescription: string;
}
export const ConnectionPageLayout: React.FC<ConnectionPageLayoutProps> = ({
  title,
  subtitle,
  icon,
  vendorType,
  filterSections,
  opportunities,
  opportunitiesLink,
  formContent,
  formCTALabel,
  formCTADescription
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  // State
  const [activeTab, setActiveTab] = useState<'all' | 'new' | 'opportunities'>(searchParams.get('tab') as 'all' | 'new' | 'opportunities' || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  // Sync tab with URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam as 'all' | 'new' | 'opportunities');
    }
  }, [searchParams]);
  const handleTabChange = (tab: 'all' | 'new' | 'opportunities') => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };
  // Filter Logic
  const filteredProfiles = useMemo(() => {
    // Base filter by vendor type
    let result = connectionProfiles.filter(p => p.vendorType === vendorType);
    // Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.role.toLowerCase().includes(q) || p.bio?.toLowerCase().includes(q) || p.specialties.some(s => s.toLowerCase().includes(q)));
    }
    // Apply custom filters passed from props
    // Note: The actual filtering logic for custom props would typically happen here
    // For this implementation, we're relying on the parent component to handle complex filter logic
    // or assuming the passed filterSections modify state that isn't fully wired in this shared layout
    // In a real app, we'd pass a filter function or lift state up.
    // For now, we'll implement basic filtering if the filter sections use standard keys like 'genre'
    // Filter for "New" tab
    if (activeTab === 'new') {
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      result = result.filter(p => {
        if (!p.joinedDate) return false;
        return new Date(p.joinedDate) >= sixtyDaysAgo;
      });
      // Sort by joined date descending
      result.sort((a, b) => {
        return new Date(b.joinedDate || 0).getTime() - new Date(a.joinedDate || 0).getTime();
      });
    }
    return result;
  }, [vendorType, searchQuery, activeTab, filterSections]); // Re-run when filters change
  return <div className={`min-h-screen ${dk.page}`} data-id="element-1195" data-mp="2xiqkkpodrzj4">
      {/* Header */}
      <div className={`pt-8 pb-6 px-4 sm:px-6 lg:px-8 ${dk.surface} border-b ${dk.border}`} data-id="element-1196" data-mp="2itkpndwksvh1">
        <div className="max-w-7xl mx-auto" data-id="element-1197" data-mp="2nse356bv4mh7">
          <div className="mb-6" data-id="element-1198" data-mp="2nrcoyszjvjv9">
            <BackButton data-id="element-1199" data-mp="2nqb4xz0k3n1e" />
          </div>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8" data-id="element-1200" data-mp="29smrvdcqdk6w">
            <div className="flex items-start gap-4" data-id="element-1201" data-mp="2es96nr87j8ht">
              <div className={`p-3 rounded-xl ${dk.isDark ? 'bg-gray-700 text-white' : 'bg-indigo-50 text-indigo-600'}`} data-id="element-1202" data-mp="2epmokxnoxalc">
                {icon}
              </div>
              <div data-id="element-1203" data-mp="24vw5676f7qci">
                <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-1204" data-mp="3u8q2haqf8aed">
                  {title}
                </h1>
                <p className={`text-lg ${dk.textSecondary}`} data-id="element-1205" data-mp="32orf26ghgsq">{subtitle}</p>
              </div>
            </div>

            <div className="w-full md:w-80" data-id="element-1206" data-mp="1nliq64u4pj5a">
              <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder={`Search ${title.replace('Connect with ', '')}...`} variant="pill" suggestions={[]} data-id="element-1207" data-mp="1ni4m8s5th1vq" />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide" data-id="element-1208" data-mp="26afjo6hpc7mg">
            <button onClick={() => handleTabChange('all')} className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg font-medium text-sm transition-colors border-b-2 ${activeTab === 'all' ? `${rt.borderAccent} ${rt.textPrimary} ${dk.isDark ? 'bg-gray-800' : 'bg-white'}` : `border-transparent ${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-1209" data-mp="229n9nb109yp4">
              <UsersIcon className="h-4 w-4" data-id="element-1210" data-mp="gw0qn9znhyl5" />
              All Profiles
            </button>
            <button onClick={() => handleTabChange('new')} className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg font-medium text-sm transition-colors border-b-2 ${activeTab === 'new' ? `${rt.borderAccent} ${rt.textPrimary} ${dk.isDark ? 'bg-gray-800' : 'bg-white'}` : `border-transparent ${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-1211" data-mp="gyde22o6h15l">
              <SparklesIcon className="h-4 w-4" data-id="element-1212" data-mp="2uvyn29waxgb9" />
              New to LitLaunch
            </button>
            <button onClick={() => handleTabChange('opportunities')} className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg font-medium text-sm transition-colors border-b-2 ${activeTab === 'opportunities' ? `${rt.borderAccent} ${rt.textPrimary} ${dk.isDark ? 'bg-gray-800' : 'bg-white'}` : `border-transparent ${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-1213" data-mp="2zv1rbs4gjii6">
              <BriefcaseIcon className="h-4 w-4" data-id="element-1214" data-mp="3ilswehhewmqr" />
              Opportunities
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1215" data-mp="1m2mihq414dj">
        {activeTab === 'opportunities' /* Opportunities Tab Content */ ? <div className="max-w-4xl mx-auto" data-id="element-1216" data-mp="3nshngppjmxul">
            <div className="flex justify-between items-center mb-6" data-id="element-1217" data-mp="3nt96z25v954i">
              <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-1218" data-mp="3jre4huc78lp5">
                Open Opportunities
              </h2>
              <Link to={opportunitiesLink} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-1219" data-mp="kapp8tktdhny">
                View all & apply filters →
              </Link>
            </div>

            <div className="grid gap-4" data-id="element-1220" data-mp="ak4slbopan7o">
              {opportunities.map(opp => <OpportunityPreviewCard key={opp.id} opportunity={opp} baseLink={opportunitiesLink} data-id="element-1221" data-mp="6jultwt33srr" />)}
            </div>

            <div className="mt-8 text-center" data-id="element-1222" data-mp="ya8u9dibwcw1">
              <Link to={opportunitiesLink} className={`inline-flex items-center px-6 py-3 rounded-lg font-medium text-white shadow-md transition-transform hover:-translate-y-0.5 ${rt.bgDark}`} data-id="element-1223" data-mp="1hzu6lszfa6u4">
                Browse All Opportunities
              </Link>
            </div>
          </div> /* Profiles Tab Content (All & New) */ : <div className="flex flex-col lg:flex-row gap-8" data-id="element-1224" data-mp="20q25fyw3ab4i">
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden mb-4" data-id="element-1225" data-mp="25ovbj691qzun">
              <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-lg shadow-sm ${dk.card} ${dk.textPrimary}`} data-id="element-1226" data-mp="25ntw6pkzzr74">
                <FilterIcon className="h-5 w-5 mr-2" data-id="element-1227" data-mp="1vt1yzmvbv6cv" />
                Filters & Sort
              </button>
            </div>

            {/* Sidebar */}
            <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-1228" data-mp="kcv8ddt30qu4">
              <FilterSidebar title="Filters" sections={filterSections} onReset={() => {
            // Reset logic would go here - typically passed from parent
            window.location.reload(); // Simple fallback for now
          }} searchable={false} data-id="element-1229" data-mp="37k9fsrhv1fe4" />
            </FilterSidebarWrapper>

            {/* Results Area */}
            <div className="flex-1 min-w-0" data-id="element-1230" data-mp="2olx8zcef8hlp">
              <div className="flex items-center justify-between mb-6" data-id="element-1231" data-mp="2omoqjjb6oesz">
                <h2 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-1232" data-mp="2jtm4rxcne9nc">
                  {filteredProfiles.length} result
                  {filteredProfiles.length !== 1 && 's'} found
                </h2>

                <div className={`flex rounded-lg p-1 border ${dk.border} ${dk.surface}`} data-id="element-1233" data-mp="1s2pun4hz2j3r">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="Grid view" data-id="element-1234" data-mp="43hkvvmyp32s">
                    <LayoutGridIcon className="h-4 w-4" data-id="element-1235" data-mp="3mdjjs5cm0r4a" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="List view" data-id="element-1236" data-mp="3he6wxklqnk1p">
                    <LayoutListIcon className="h-4 w-4" data-id="element-1237" data-mp="2ylmhp5yypdgd" />
                  </button>
                </div>
              </div>

              {filteredProfiles.length > 0 ? <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-1238" data-mp="2tnb5wpp3wsvi">
                  {filteredProfiles.map(profile => <ProfileCard key={profile.id} data={profile} viewMode={viewMode} data-id="element-1239" data-mp="1j2oldy4c8uh9" />)}
                </div> : <div className={`text-center py-16 rounded-xl border-2 border-dashed ${dk.border} ${dk.surfaceMuted}`} data-id="element-1240" data-mp="qesgw78j1y9x">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-1241" data-mp="vapuw6wbisjj">
                    <SearchIcon className={`h-8 w-8 ${dk.textMuted}`} data-id="element-1242" data-mp="147ieza1bytne" />
                  </div>
                  <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-1243" data-mp="7lf33ewy48hj">
                    No profiles found
                  </h3>
                  <p className={`max-w-md mx-auto ${dk.textSecondary}`} data-id="element-1244" data-mp="1ezh38dtx2djg">
                    Try adjusting your filters or search terms.
                  </p>
                </div>}
            </div>
          </div>}

        {/* Bottom CTA / Form Toggle */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-700" data-id="element-1245" data-mp="1jqqrb9zth8t8">
          <div className={`rounded-2xl overflow-hidden shadow-lg border ${dk.border} ${dk.isDark ? 'bg-gray-800' : 'bg-indigo-50'}`} data-id="element-1246" data-mp="1jstqajiy6ca3">
            <div className="p-8 text-center" data-id="element-1247" data-mp="r33b075kx4ce">
              <h2 className={`text-2xl font-bold mb-3 ${dk.textHeading}`} data-id="element-1248" data-mp="r4egqcil4m65">
                {formCTALabel}
              </h2>
              <p className={`mb-6 max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-1249" data-mp="10zpvgutzttdw">
                {formCTADescription}
              </p>
              <button onClick={() => setIsFormOpen(!isFormOpen)} className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-all ${isFormOpen ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200' : `${rt.primaryButton} text-white shadow-md hover:-translate-y-0.5`}`} data-id="element-1250" data-mp="2gfoo0xr4ngt1">
                {isFormOpen ? <>
                    Close Request Form{' '}
                    <ChevronUpIcon className="ml-2 h-4 w-4" data-id="element-1251" data-mp="2ezaug0r9a1mx" />
                  </> : <>
                    Start a Request <ChevronDownIcon className="ml-2 h-4 w-4" data-id="element-1252" data-mp="32px1bopixfvc" />
                  </>}
              </button>
            </div>

            {/* Collapsible Form Content */}
            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isFormOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1253" data-mp="2syu1src8t8y6">
              <div className={`p-6 sm:p-10 border-t ${dk.border} ${dk.surface}`} data-id="element-1254" data-mp="3lo0xlikbbkfq">
                {formContent}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};